/**
 * Authentication service for handling auth-related API calls
 */

import { api, tokenManager } from './api'
import { User } from '@shared/types'
import { transformUser, BackendUser } from '@/utils/userTransform'
import { 
  type LoginRequest, 
  type RegisterRequest
} from '@shared'

// Backend Auth Response (actual response format with message)
export interface BackendAuthResponse {
  message: string
  user: BackendUser
  token: string
}

// Profile response type
export interface ProfileResponse {
  user: BackendUser
}

export interface RefreshResponse {
  message: string
  user: BackendUser
  token: string
}

export interface LogoutResponse {
  message: string
}

/**
 * Authentication service class
 */
export class AuthService {
  private static readonly AUTH_ENDPOINTS = {
    LOGIN: '/auth/login',
    REGISTER: '/auth/register',
    LOGOUT: '/auth/logout',
    REFRESH: '/auth/refresh',
    PROFILE: '/auth/me',
  } as const

  /**
   * Login user with email and password
   */
  static async login(credentials: LoginRequest): Promise<{ user: User; token: string; message: string }> {
    try {
      const response = await api.post<BackendAuthResponse>(
        this.AUTH_ENDPOINTS.LOGIN,
        credentials
      )

      // Store token in localStorage upon successful login
      if (response.token) {
        tokenManager.setToken(response.token)
      }

      // Transform backend user to frontend user format
      const user = transformUser(response.user)

      return {
        user,
        token: response.token,
        message: response.message
      }
    } catch (error) {
      // Ensure token is removed on login failure
      tokenManager.removeToken()
      throw error
    }
  }

  /**
   * Register new user
   */
  static async register(userData: RegisterRequest): Promise<{ user: User; token: string; message: string }> {
    try {
      const response = await api.post<BackendAuthResponse>(
        this.AUTH_ENDPOINTS.REGISTER,
        userData
      )

      // Store token in localStorage upon successful registration
      if (response.token) {
        tokenManager.setToken(response.token)
      }

      // Transform backend user to frontend user format
      const user = transformUser(response.user)

      return {
        user,
        token: response.token,
        message: response.message
      }
    } catch (error) {
      // Ensure token is removed on registration failure
      tokenManager.removeToken()
      throw error
    }
  }

  /**
   * Logout user (client-side token removal + server notification)
   */
  static async logout(): Promise<LogoutResponse> {
    try {
      // Call logout endpoint if token exists
      const token = tokenManager.getToken()
      if (token) {
        const response = await api.post<LogoutResponse>(this.AUTH_ENDPOINTS.LOGOUT)
        return response
      }
      return { message: 'Logged out successfully' }
    } finally {
      // Always remove token regardless of server response
      tokenManager.removeToken()
    }
  }

  /**
   * Refresh JWT token
   */
  static async refreshToken(): Promise<{ user: User; token: string; message: string }> {
    try {
      const response = await api.post<RefreshResponse>(this.AUTH_ENDPOINTS.REFRESH)

      // Update stored token
      if (response.token) {
        tokenManager.setToken(response.token)
      }

      // Transform backend user to frontend user format
      const user = transformUser(response.user)

      return {
        user,
        token: response.token,
        message: response.message
      }
    } catch (error) {
      // Remove invalid token on refresh failure
      tokenManager.removeToken()
      throw error
    }
  }

  /**
   * Get current user profile
   */
  static async getProfile(): Promise<{ user: User }> {
    const response = await api.get<ProfileResponse>(this.AUTH_ENDPOINTS.PROFILE)
    
    // Transform backend user to frontend user format
    const user = transformUser(response.user)
    
    return { user }
  }

  /**
   * Check if user is authenticated (has valid token)
   */
  static isAuthenticated(): boolean {
    return tokenManager.isTokenValid()
  }

  /**
   * Get current auth token
   */
  static getToken(): string | null {
    return tokenManager.getToken()
  }

  /**
   * Validate token and get user info if valid
   */
  static async validateSession(): Promise<User | null> {
    try {
      if (!this.isAuthenticated()) {
        return null
      }

      const response = await this.getProfile()
      return response.user
    } catch (error) {
      // Token might be invalid, remove it
      tokenManager.removeToken()
      return null
    }
  }

  /**
   * Handle automatic token refresh
   */
  static async handleTokenRefresh(): Promise<{ user: User; token: string; message: string } | null> {
    try {
      const token = tokenManager.getToken()
      if (!token) return null

      // Check if token is close to expiring (refresh if < 5 minutes left)
      const payload = JSON.parse(atob(token.split('.')[1]))
      const currentTime = Math.floor(Date.now() / 1000)
      const timeUntilExpiry = payload.exp - currentTime

      if (timeUntilExpiry < 300) { // 5 minutes
        const response = await this.refreshToken()
        return response
      }

      return null
    } catch (error) {
      console.warn('Token refresh failed:', error)
      return null
    }
  }
}

// Export as default and named export for convenience
export default AuthService 